import { useState } from 'react';
import { useRouteLoaderData } from 'react-router';

import {
    getPendingHeroImage,
    PROFESSIONAL_HERO_TRANSITION_NAME,
    useIsDesktopViewport,
    useTileHeroViewTransitionStyle,
} from '@rocco/ui/tile-shared/hooks/use-tile-hero-view-transition';
import type { loader as layoutLoader } from '@modules/professional/page/loaders/professional-page-loader.server';
import { useProfileFamily } from '@modules/professional/hook/use-profile-family';
import { DetailHero } from '~/ui-rocco/pages/detail-shared';
import { ProfileFamilyMarks } from './ProfileFamilyMarks';

/**
 * Desktop professional hero — the magazine-cover banner with the professional
 * name painted over cross-fading banner images and the logo badge bottom-right
 * (Figma / BC-4505). Reads the shared layout loader so it renders once in the
 * persistent layout and never re-flashes on tab navigation. Desktop only; mobile
 * uses the social-profile {@link import('./ProfessionalProfileHero')} +
 * {@link import('./ProfessionalProfileHeader')} instead.
 *
 * The banner box is the morph counterpart of a listing professional tile
 * (`professional-hero`). The claim matches the profile ROOT link, not the
 * current pathname — the hero persists across tab routes, and leaving a tab
 * back to a listing must not produce a one-sided claim.
 */
export const ProfessionalHeroSection = () => {
    const layoutData = useRouteLoaderData<typeof layoutLoader>(
        'routes/_app.professional.$urlSegment',
    );
    const profileLink = layoutData?.initial.Link;
    // Desktop-only morph, mirroring the tile side: on mobile this banner is
    // far smaller than the tile, so neither side claims the name there.
    const isDesktopViewport = useIsDesktopViewport();
    const heroStyle = useTileHeroViewTransitionStyle(
        isDesktopViewport ? profileLink : undefined,
        PROFESSIONAL_HERO_TRANSITION_NAME,
    );
    const [underlaySrc] = useState(() =>
        profileLink ? getPendingHeroImage(profileLink) : null,
    );
    const family = useProfileFamily();
    if (!layoutData) return null;
    const { initial, banners } = layoutData;

    const images = banners
        .map((banner) => banner.libraryImageFile?.DetailBannerStandard?.Desktop)
        .filter((src): src is string => Boolean(src));

    // Small first-banner rendition as the hero's base layer: the full-size
    // banner is the page's largest asset, so the hero paints near-instantly
    // and sharpens rather than sitting on the dark slab.
    const firstBannerFile = banners[0]?.libraryImageFile;
    const heroBaseSrc =
        firstBannerFile?.ThumbLandscapeDesktop ??
        firstBannerFile?.ThumbLandscapeMobile ??
        null;

    return (
        <DetailHero
            title={initial.Title}
            backgroundImages={images}
            // The banner stands taller than the viewport, so the name rides the
            // bottom of the screen while it scrolls past rather than leaving
            // with the first screenful.
            stickyTitle
            heroStyle={heroStyle}
            heroUnderlaySrc={underlaySrc}
            heroBaseSrc={heroBaseSrc}
            logo={
                initial.CompanyLogo
                    ? {
                          src: initial.CompanyLogo,
                          background: initial.LogoBackgroundColor,
                          link: initial.Link,
                      }
                    : undefined
            }
            // Figma 2354:58647 — 172px badge sitting 88px from the container's
            // right edge and hanging 65px below the banner. rem so it anchors
            // to the centred 1920 container on wider screens (matches vw at
            // every width below the cap).
            logoClassName="md:-bottom-[4.05rem] md:right-[5.52rem] md:size-[10.75rem]"
            // Figma 5498:66909 — the family marks fan out to the badge's left,
            // sized and offset as ratios of the badge so the cluster keeps its
            // proportions: marks at 51% of it, a 11% gap before it, each mark
            // overlapping the next by 14%, all centred on the badge's midline.
            logoAside={
                family ? (
                    <ProfileFamilyMarks
                        brands={family.others}
                        className="absolute z-10 -space-x-[0.761rem] max-md:hidden md:right-[17.476rem] md:-bottom-[1.422rem]"
                        markClassName="md:size-[5.494rem]"
                    />
                ) : null
            }
        />
    );
};
